import { useEffect, useState } from "react";

/**
 * React hook that tracks the current `window.innerWidth` and updates on
 * resize.  Used by responsive components that need to recalculate layout or
 * data whenever the viewport width changes (e.g. Navigation Minimap tick
 * generation).
 */
export function useWindowWidth(initialWidth: number = 1200): number {
  // Default to a sane width during SSR or tests where `window` is undefined.
  const [width, setWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : initialWidth,
  );

  useEffect(() => {
    // Guard for environments where `window` might not exist.
    if (typeof window === "undefined") return;

    // Throttle updates with requestAnimationFrame so we only update
    // at most once per frame, even if the user drags the browser
    // size continuously.
    let frameId: number | null = null;

    const handleResize = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(() => {
        setWidth(window.innerWidth);
        frameId = null;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return width;
}
